// Procedural textures owned by the loot system: decorative layers (segmented
// orb rims, hologram beam, scanlines, glints) plus fallbacks for shared keys
// (orb shells, holo base, scrap, health, item icons) used only while the art
// modules have not produced them. Everything is drawn once with Canvas2D.

import type Phaser from 'phaser';
import { TEX } from '../../art/keys';
import { COLORS, css, mix, shade } from '../../config/palette';
import type { EquipSlot, ItemKind, OrbType } from '../../core/types';

export const LOOT_TEX = {
  shell: (t: OrbType) => `loot_orb_shell_${t}`,
  rim: 'loot_orb_rim',
  rimInner: 'loot_orb_rim_inner',
  soft: 'loot_soft',
  dot: 'loot_dot',
  shadow: 'loot_shadow',
  streak: 'loot_streak',
  holoBase: 'loot_holo_base',
  emitter: 'loot_holo_emitter',
  beam: 'loot_holo_beam',
  scan: 'loot_scanlines',
  sweep: 'loot_sweep',
  scrap: 'loot_scrap',
  health: 'loot_health',
  panel: 'loot_panel_px',
  icon: (k: string) => `loot_icon_${k}`,
} as const;

export const ORB_COLOR: Readonly<Record<OrbType, number>> = {
  arsenal: COLORS.orbArsenal,
  armor: COLORS.orbArmor,
  unstable: COLORS.orbUnstable,
};

type Ctx = CanvasRenderingContext2D;

function canvas(scene: Phaser.Scene, key: string, w: number, h: number, draw: (c: Ctx, w: number, h: number) => void): void {
  if (scene.textures.exists(key)) return;
  const tex = scene.textures.createCanvas(key, w, h);
  if (!tex) return;
  const c = tex.getContext();
  c.clearRect(0, 0, w, h);
  draw(c, w, h);
  tex.refresh();
}

function circle(c: Ctx, x: number, y: number, r: number): void {
  c.beginPath();
  c.arc(x, y, r, 0, Math.PI * 2);
}

// ───────────────────────────── Orb ─────────────────────────────

function drawShell(c: Ctx, size: number, color: number): void {
  const cx = size / 2;
  const cy = size / 2;
  const r = size * 0.42;
  // Glass body: dark core, bright fresnel edge.
  const body = c.createRadialGradient(cx, cy, 0, cx, cy, r);
  body.addColorStop(0, css(mix(color, COLORS.ink, 0.8), 0.94));
  body.addColorStop(0.6, css(mix(color, COLORS.ink, 0.64), 0.95));
  body.addColorStop(0.88, css(mix(color, COLORS.ink, 0.22), 0.98));
  body.addColorStop(1, css(color, 1));
  c.fillStyle = body;
  circle(c, cx, cy, r);
  c.fill();

  c.save();
  circle(c, cx, cy, r);
  c.clip();
  // Subsurface glow pooling at the bottom.
  const sub = c.createRadialGradient(cx, cy + r * 0.55, 0, cx, cy + r * 0.55, r * 0.85);
  sub.addColorStop(0, css(color, 0.6));
  sub.addColorStop(1, css(color, 0));
  c.fillStyle = sub;
  c.fillRect(0, 0, size, size);
  // Latitude lines give the sphere volume.
  c.strokeStyle = css(mix(color, 0xffffff, 0.35), 0.2);
  c.lineWidth = 1.2;
  for (const k of [-0.45, 0, 0.45]) {
    c.beginPath();
    c.ellipse(cx, cy + r * k, r * Math.sqrt(1 - k * k), r * 0.22 * Math.sqrt(1 - k * k), 0, 0, Math.PI * 2);
    c.stroke();
  }
  c.beginPath();
  c.ellipse(cx, cy, r * 0.35, r, 0, 0, Math.PI * 2);
  c.stroke();
  // Specular bloom, top-left.
  const spec = c.createRadialGradient(cx - r * 0.4, cy - r * 0.45, 0, cx - r * 0.4, cy - r * 0.45, r * 0.55);
  spec.addColorStop(0, 'rgba(255,255,255,0.7)');
  spec.addColorStop(0.45, 'rgba(255,255,255,0.14)');
  spec.addColorStop(1, 'rgba(255,255,255,0)');
  c.fillStyle = spec;
  c.fillRect(0, 0, size, size);
  c.restore();

  // Inner tech ring + crisp rim.
  c.strokeStyle = css(color, 0.45);
  c.lineWidth = 1.5;
  circle(c, cx, cy, r * 0.8);
  c.stroke();
  c.strokeStyle = css(mix(color, 0xffffff, 0.6), 0.95);
  c.lineWidth = 2.2;
  circle(c, cx, cy, r - 1.1);
  c.stroke();
  c.fillStyle = 'rgba(255,255,255,0.92)';
  c.beginPath();
  c.ellipse(cx - r * 0.44, cy - r * 0.5, r * 0.13, r * 0.07, -0.7, 0, Math.PI * 2);
  c.fill();
}

function drawRim(c: Ctx, size: number): void {
  const cx = size / 2;
  const r = size * 0.45;
  c.strokeStyle = '#ffffff';
  c.lineCap = 'round';
  c.lineWidth = 3.4;
  for (let i = 0; i < 3; i++) {
    const a0 = i * ((Math.PI * 2) / 3) + 0.2;
    c.beginPath();
    c.arc(cx, cx, r, a0, a0 + 1.35);
    c.stroke();
    // Bright end caps.
    for (const a of [a0, a0 + 1.35]) {
      c.fillStyle = '#ffffff';
      circle(c, cx + Math.cos(a) * r, cx + Math.sin(a) * r, 3.2);
      c.fill();
    }
  }
  // Fine ticks in the gaps.
  c.lineWidth = 1.6;
  for (let i = 0; i < 36; i++) {
    const a = (i / 36) * Math.PI * 2;
    const seg = (((a - 0.2) % ((Math.PI * 2) / 3)) + (Math.PI * 2) / 3) % ((Math.PI * 2) / 3);
    if (seg < 1.45) continue;
    c.globalAlpha = 0.7;
    c.beginPath();
    c.moveTo(cx + Math.cos(a) * (r - 5), cx + Math.sin(a) * (r - 5));
    c.lineTo(cx + Math.cos(a) * (r + 1), cx + Math.sin(a) * (r + 1));
    c.stroke();
  }
  c.globalAlpha = 1;
}

function drawRimInner(c: Ctx, size: number): void {
  const cx = size / 2;
  const r = size * 0.4;
  c.strokeStyle = '#ffffff';
  c.lineWidth = 1.8;
  c.lineCap = 'butt';
  for (let i = 0; i < 10; i++) {
    const a0 = (i / 10) * Math.PI * 2;
    c.globalAlpha = i % 2 === 0 ? 0.9 : 0.45;
    c.beginPath();
    c.arc(cx, cx, r, a0, a0 + 0.38);
    c.stroke();
  }
  c.globalAlpha = 1;
}

// ───────────────────────────── Generic glows ─────────────────────────────

function drawSoft(c: Ctx, size: number): void {
  const h = size / 2;
  const g = c.createRadialGradient(h, h, 0, h, h, h);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.22, 'rgba(255,255,255,0.62)');
  g.addColorStop(0.55, 'rgba(255,255,255,0.16)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  c.fillStyle = g;
  c.fillRect(0, 0, size, size);
}

function drawShadow(c: Ctx, w: number, h: number): void {
  c.save();
  c.scale(1, h / w);
  const r = w / 2;
  const g = c.createRadialGradient(r, r, 0, r, r, r);
  g.addColorStop(0, 'rgba(0,0,0,0.6)');
  g.addColorStop(0.6, 'rgba(0,0,0,0.25)');
  g.addColorStop(1, 'rgba(0,0,0,0)');
  c.fillStyle = g;
  c.fillRect(0, 0, w, w);
  c.restore();
}

function drawStreak(c: Ctx, w: number, h: number): void {
  const g = c.createLinearGradient(0, 0, w, 0);
  g.addColorStop(0, 'rgba(255,255,255,0)');
  g.addColorStop(0.5, 'rgba(255,255,255,1)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  const v = c.createLinearGradient(0, 0, 0, h);
  v.addColorStop(0, 'rgba(0,0,0,1)');
  v.addColorStop(0.5, 'rgba(0,0,0,0)');
  v.addColorStop(1, 'rgba(0,0,0,1)');
  c.fillStyle = g;
  c.fillRect(0, 0, w, h);
  // Fade the top and bottom edges out.
  c.globalCompositeOperation = 'destination-out';
  c.fillStyle = v;
  c.fillRect(0, 0, w, h);
  c.globalCompositeOperation = 'source-over';
}

// ───────────────────────────── Hologram ─────────────────────────────

function drawHoloBase(c: Ctx, w: number, h: number): void {
  const top = 14;
  // Pedestal body.
  c.beginPath();
  c.moveTo(18, top);
  c.lineTo(w - 18, top);
  c.lineTo(w - 4, h - 10);
  c.lineTo(w - 4, h - 2);
  c.lineTo(4, h - 2);
  c.lineTo(4, h - 10);
  c.closePath();
  const g = c.createLinearGradient(0, top, 0, h);
  g.addColorStop(0, css(COLORS.steel4));
  g.addColorStop(0.18, css(COLORS.steel3));
  g.addColorStop(0.55, css(COLORS.steel1));
  g.addColorStop(1, css(COLORS.steel0));
  c.fillStyle = g;
  c.fill();
  c.strokeStyle = css(COLORS.ink);
  c.lineWidth = 2;
  c.stroke();
  // Panel seams + status lights.
  c.strokeStyle = css(COLORS.ink, 0.8);
  c.lineWidth = 1;
  for (const x of [w * 0.3, w * 0.7]) {
    c.beginPath();
    c.moveTo(x, top + 6);
    c.lineTo(x + (x < w / 2 ? -5 : 5), h - 3);
    c.stroke();
  }
  c.fillStyle = css(COLORS.steel5, 0.9);
  for (const x of [w * 0.16, w * 0.84]) c.fillRect(x - 2, h - 8, 4, 2);
  // Emitter lens on top.
  c.fillStyle = css(COLORS.steel0);
  c.beginPath();
  c.ellipse(w / 2, top, w * 0.36, 6, 0, 0, Math.PI * 2);
  c.fill();
  c.strokeStyle = css(COLORS.steel5, 0.85);
  c.lineWidth = 1.4;
  c.stroke();
}

function drawEmitter(c: Ctx, w: number, h: number): void {
  c.save();
  c.translate(w / 2, h / 2);
  c.scale(1, h / w);
  const r = w / 2;
  const g = c.createRadialGradient(0, 0, 0, 0, 0, r);
  g.addColorStop(0, 'rgba(255,255,255,0.9)');
  g.addColorStop(0.45, 'rgba(255,255,255,0.55)');
  g.addColorStop(0.7, 'rgba(255,255,255,0.85)');
  g.addColorStop(0.78, 'rgba(255,255,255,0.25)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  c.fillStyle = g;
  c.beginPath();
  c.arc(0, 0, r, 0, Math.PI * 2);
  c.fill();
  c.restore();
}

function drawBeam(c: Ctx, w: number, h: number): void {
  const bottomHalf = w * 0.3;
  c.beginPath();
  c.moveTo(w / 2 - bottomHalf, h);
  c.lineTo(w / 2 + bottomHalf, h);
  c.lineTo(w, 0);
  c.lineTo(0, 0);
  c.closePath();
  const g = c.createLinearGradient(0, h, 0, 0);
  g.addColorStop(0, 'rgba(255,255,255,0.75)');
  g.addColorStop(0.35, 'rgba(255,255,255,0.28)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  c.fillStyle = g;
  c.fill();
  // Edge lines.
  c.strokeStyle = 'rgba(255,255,255,0.35)';
  c.lineWidth = 1;
  c.beginPath();
  c.moveTo(w / 2 - bottomHalf, h);
  c.lineTo(2, 0);
  c.moveTo(w / 2 + bottomHalf, h);
  c.lineTo(w - 2, 0);
  c.stroke();
}

function drawScanlines(c: Ctx, w: number, h: number): void {
  for (let y = 0; y < h; y += 4) {
    c.fillStyle = y % 16 === 0 ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.5)';
    c.fillRect(0, y, w, 1.4);
  }
}

function drawSweep(c: Ctx, w: number, h: number): void {
  const g = c.createLinearGradient(0, 0, 0, h);
  g.addColorStop(0, 'rgba(255,255,255,0)');
  g.addColorStop(0.5, 'rgba(255,255,255,1)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  c.fillStyle = g;
  c.fillRect(0, 0, w, h);
}

// ───────────────────────────── Pickups ─────────────────────────────

function drawScrap(c: Ctx, w: number, h: number): void {
  const pts: [number, number][] = [
    [3, 9],
    [9, 2],
    [18, 3],
    [w - 2, 10],
    [17, h - 2],
    [6, h - 3],
  ];
  c.beginPath();
  pts.forEach(([x, y], i) => (i === 0 ? c.moveTo(x, y) : c.lineTo(x, y)));
  c.closePath();
  const g = c.createLinearGradient(0, 0, w, h);
  g.addColorStop(0, css(mix(COLORS.scrap, 0xffffff, 0.5)));
  g.addColorStop(0.45, css(COLORS.scrap));
  g.addColorStop(1, css(shade(COLORS.scrap, 0.5)));
  c.fillStyle = g;
  c.fill();
  c.strokeStyle = css(COLORS.ink);
  c.lineWidth = 1.5;
  c.stroke();
  c.fillStyle = css(COLORS.ink, 0.85);
  circle(c, 12, 10, 2.4);
  c.fill();
  c.strokeStyle = 'rgba(255,255,255,0.75)';
  c.lineWidth = 1.2;
  c.beginPath();
  c.moveTo(9, 3.5);
  c.lineTo(17.5, 4.5);
  c.stroke();
}

function roundRect(c: Ctx, x: number, y: number, w: number, h: number, r: number): void {
  c.beginPath();
  c.moveTo(x + r, y);
  c.arcTo(x + w, y, x + w, y + h, r);
  c.arcTo(x + w, y + h, x, y + h, r);
  c.arcTo(x, y + h, x, y, r);
  c.arcTo(x, y, x + w, y, r);
  c.closePath();
}

function drawHealth(c: Ctx, w: number): void {
  roundRect(c, 3, 3, w - 6, w - 6, 8);
  const g = c.createLinearGradient(0, 0, 0, w);
  g.addColorStop(0, css(mix(COLORS.heal, COLORS.ink, 0.45)));
  g.addColorStop(1, css(mix(COLORS.heal, COLORS.ink, 0.78)));
  c.fillStyle = g;
  c.fill();
  c.strokeStyle = css(COLORS.heal);
  c.lineWidth = 2;
  c.stroke();
  const m = w / 2;
  c.fillStyle = css(mix(COLORS.heal, 0xffffff, 0.35));
  c.fillRect(m - 3.5, 8, 7, w - 16);
  c.fillRect(8, m - 3.5, w - 16, 7);
  c.fillStyle = 'rgba(255,255,255,0.55)';
  c.fillRect(6, 6, w - 14, 2);
}

// ───────────────────────────── Icon fallbacks (white, tinted at runtime) ─────────────────────────────

const ICON_PAINTERS: Record<string, (c: Ctx, s: number) => void> = {
  weapon: (c, s) => {
    c.fillRect(s * 0.06, s * 0.4, s * 0.22, s * 0.14); // stock
    c.fillRect(s * 0.24, s * 0.34, s * 0.42, s * 0.22); // receiver
    c.fillRect(s * 0.62, s * 0.4, s * 0.32, s * 0.08); // barrel
    c.save();
    c.translate(s * 0.4, s * 0.54);
    c.rotate(0.25);
    c.fillRect(0, 0, s * 0.11, s * 0.24); // grip
    c.restore();
    c.fillRect(s * 0.52, s * 0.28, s * 0.12, s * 0.07); // sight
  },
  chest: (c, s) => {
    c.beginPath();
    c.moveTo(s * 0.2, s * 0.18);
    c.lineTo(s * 0.8, s * 0.18);
    c.lineTo(s * 0.86, s * 0.48);
    c.lineTo(s * 0.5, s * 0.86);
    c.lineTo(s * 0.14, s * 0.48);
    c.closePath();
    c.fill();
    c.globalCompositeOperation = 'destination-out';
    c.fillRect(s * 0.47, s * 0.26, s * 0.06, s * 0.46);
    c.globalCompositeOperation = 'source-over';
  },
  shoulders: (c, s) => {
    c.beginPath();
    c.arc(s * 0.5, s * 0.62, s * 0.36, Math.PI, 0);
    c.lineTo(s * 0.86, s * 0.74);
    c.lineTo(s * 0.14, s * 0.74);
    c.closePath();
    c.fill();
    c.globalCompositeOperation = 'destination-out';
    for (const x of [0.32, 0.5, 0.68]) {
      c.beginPath();
      c.arc(s * x, s * 0.5, s * 0.05, 0, Math.PI * 2);
      c.fill();
    }
    c.globalCompositeOperation = 'source-over';
  },
  back: (c, s) => {
    roundRect(c, s * 0.22, s * 0.12, s * 0.56, s * 0.72, s * 0.12);
    c.fill();
    c.globalCompositeOperation = 'destination-out';
    c.beginPath();
    c.arc(s * 0.5, s * 0.44, s * 0.14, 0, Math.PI * 2);
    c.fill();
    c.fillRect(s * 0.32, s * 0.66, s * 0.36, s * 0.05);
    c.globalCompositeOperation = 'source-over';
    c.beginPath();
    c.arc(s * 0.5, s * 0.44, s * 0.07, 0, Math.PI * 2);
    c.fill();
  },
  legs: (c, s) => {
    c.beginPath();
    c.moveTo(s * 0.34, s * 0.1);
    c.lineTo(s * 0.62, s * 0.1);
    c.lineTo(s * 0.6, s * 0.62);
    c.lineTo(s * 0.86, s * 0.74);
    c.lineTo(s * 0.86, s * 0.88);
    c.lineTo(s * 0.26, s * 0.88);
    c.lineTo(s * 0.3, s * 0.62);
    c.closePath();
    c.fill();
  },
  mod: (c, s) => {
    roundRect(c, s * 0.24, s * 0.24, s * 0.52, s * 0.52, s * 0.06);
    c.fill();
    for (let i = 0; i < 4; i++) {
      const p = s * (0.3 + i * 0.13);
      c.fillRect(p, s * 0.1, s * 0.06, s * 0.12);
      c.fillRect(p, s * 0.78, s * 0.06, s * 0.12);
      c.fillRect(s * 0.1, p, s * 0.12, s * 0.06);
      c.fillRect(s * 0.78, p, s * 0.12, s * 0.06);
    }
    c.globalCompositeOperation = 'destination-out';
    roundRect(c, s * 0.36, s * 0.36, s * 0.28, s * 0.28, s * 0.04);
    c.fill();
    c.globalCompositeOperation = 'source-over';
  },
  mutation: (c, s) => {
    c.lineWidth = s * 0.07;
    c.lineCap = 'round';
    for (const phase of [0, Math.PI]) {
      c.beginPath();
      for (let i = 0; i <= 24; i++) {
        const t = i / 24;
        const x = s * 0.5 + Math.sin(t * Math.PI * 2 + phase) * s * 0.22;
        const y = s * (0.12 + t * 0.76);
        if (i === 0) c.moveTo(x, y);
        else c.lineTo(x, y);
      }
      c.stroke();
    }
    c.lineWidth = s * 0.04;
    for (let i = 1; i < 6; i++) {
      const t = i / 6;
      const dx = Math.sin(t * Math.PI * 2) * s * 0.22;
      c.beginPath();
      c.moveTo(s * 0.5 - dx, s * (0.12 + t * 0.76));
      c.lineTo(s * 0.5 + dx, s * (0.12 + t * 0.76));
      c.stroke();
    }
  },
};

/** Fallback icon key for an item kind / equipment slot. */
export function fallbackIconKey(scene: Phaser.Scene, kind: ItemKind, slot?: EquipSlot): string {
  const name = kind === 'equipment' ? slot ?? 'chest' : kind === 'weapon' ? 'weapon' : kind;
  const key = LOOT_TEX.icon(name);
  canvas(scene, key, 64, 64, (c, s) => {
    c.fillStyle = '#ffffff';
    c.strokeStyle = '#ffffff';
    (ICON_PAINTERS[name] ?? ICON_PAINTERS.mod)(c, s);
  });
  return key;
}

// ───────────────────────────── Public ─────────────────────────────

/** Create every loot-owned texture once per game (idempotent). */
export function ensureLootTextures(scene: Phaser.Scene): void {
  for (const t of ['arsenal', 'armor', 'unstable'] as OrbType[]) {
    canvas(scene, LOOT_TEX.shell(t), 96, 96, (c, s) => drawShell(c, s, ORB_COLOR[t]));
  }
  canvas(scene, LOOT_TEX.rim, 128, 128, (c, s) => drawRim(c, s));
  canvas(scene, LOOT_TEX.rimInner, 128, 128, (c, s) => drawRimInner(c, s));
  canvas(scene, LOOT_TEX.soft, 64, 64, (c, s) => drawSoft(c, s));
  canvas(scene, LOOT_TEX.dot, 16, 16, (c, s) => drawSoft(c, s));
  canvas(scene, LOOT_TEX.shadow, 128, 32, drawShadow);
  canvas(scene, LOOT_TEX.streak, 256, 24, drawStreak);
  canvas(scene, LOOT_TEX.holoBase, 128, 40, drawHoloBase);
  canvas(scene, LOOT_TEX.emitter, 128, 28, drawEmitter);
  canvas(scene, LOOT_TEX.beam, 96, 128, drawBeam);
  canvas(scene, LOOT_TEX.scan, 64, 64, drawScanlines);
  canvas(scene, LOOT_TEX.sweep, 96, 20, drawSweep);
  canvas(scene, LOOT_TEX.scrap, 24, 20, drawScrap);
  canvas(scene, LOOT_TEX.health, 32, 32, (c, s) => drawHealth(c, s));
  canvas(scene, LOOT_TEX.panel, 4, 4, (c) => {
    c.fillStyle = '#ffffff';
    c.fillRect(0, 0, 4, 4);
  });
}

/** Shared key if the art module produced it, else the loot fallback. */
export function texOr(scene: Phaser.Scene, key: string, fallback: string): string {
  return scene.textures.exists(key) ? key : fallback;
}

/** Soft glow: the shared FX texture when present. */
export function softKey(scene: Phaser.Scene): string {
  return texOr(scene, TEX.soft, LOOT_TEX.soft);
}
